import React from 'react';
import { motion } from 'framer-motion';
import { Gift, CheckCircle2, Printer, Calendar, BookOpen, Clock, Shield } from 'lucide-react';
interface Bonus {
  number: number;
  title: string;
  subtitle?: string;
  value: string;
  description: string;
  checklist: string[];
  image: string;
  icon: React.ReactNode;
}
const bonuses: Bonus[] = [{
  number: 1,
  title: 'Pack de Plantillas Imprimibles',
  value: '$15.000',
  description: '+36 plantillas visuales listas para imprimir y usar. Los niños ven claramente qué deben hacer en cada momento del día.',
  checklist: ['Rutinas de mañana, tarde y noche', 'Tarjetas recortables', 'Tableros personalizables', 'Sistema Primero-Después', 'Adaptadas por edades'],
  image: 'https://cdn.magicpatterns.com/uploads/5CTa398g7wk214PRhY77pC/bono_1.png',
  icon: <Printer className="w-4 h-4" data-id="element-1" />
}, {
  number: 2,
  title: 'Desafío 7 Días Sin Gritos',
  value: '$12.000',
  description: 'Un desafío guiado de 7 días para implementar el sistema paso a paso con acciones de menos de 10 minutos diarios.',
  checklist: ['Introducir rutinas visuales correctamente', 'Reducir conflictos diarios', 'Crear cooperación sin gritos', 'Construir hábitos sostenibles'],
  image: 'https://cdn.magicpatterns.com/uploads/4GQftiZyAKg81uaw52pv2k/bono_2.png',
  icon: <Calendar className="w-4 h-4" data-id="element-2" />
}, {
  number: 3,
  title: 'El Mapa de la Calma',
  subtitle: '5 cuentos para cooperar sin gritos',
  value: '$10.000',
  description: '5 cuentos diseñados para ayudar a los niños a comprender rutinas, emociones y cooperación de manera natural.',
  checklist: ['Entender mejor las rutinas', 'Reducir resistencia al cambio', 'Regular emociones', 'Convertir conflicto en conexión'],
  image: 'https://cdn.magicpatterns.com/uploads/cnSpPWrmy1Sy1amAdDYS2R/bono_3.png',
  icon: <BookOpen className="w-4 h-4" data-id="element-3" />
}, {
  number: 4,
  title: 'Sistema Minimalista',
  subtitle: 'Resultados con 10 minutos al día',
  value: '$11.000',
  description: 'Versión simplificada del método para aplicarse en solo 10 minutos al día. Perfecto para familias ocupadas.',
  checklist: ['Simplificar rutinas familiares', 'Reducir fricción diaria', 'Mantener consistencia sin esfuerzo', 'Aplicar incluso en días ocupados'],
  image: 'https://cdn.magicpatterns.com/uploads/7uYddgivUDx1VvHcioQESB/bono_4.png',
  icon: <Clock className="w-4 h-4" data-id="element-4" />
}, {
  number: 5,
  title: 'Protocolo Antirabietas',
  subtitle: 'Transiciones sin explosiones',
  value: '$11.000',
  description: 'Protocolo práctico para manejar transiciones difíciles (pantallas, salir de casa, dormir) de forma calmada y efectiva.',
  checklist: ['Anticipar transiciones', 'Qué decir cuando se resiste', 'Responder durante rabietas sin perder la calma', 'Recuperar regulación emocional'],
  image: 'https://cdn.magicpatterns.com/uploads/bunSmYt2syXLrroC8PueME/bono_5.png',
  icon: <Shield className="w-4 h-4" data-id="element-5" />
}];
function BonusCard({
  bonus,
  index
}: {
  bonus: Bonus;
  index: number;
}) {
  const isEven = index % 2 === 1;
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: index * 0.1
  }} className="bg-white rounded-3xl shadow-lg overflow-hidden border border-stone-100" data-id="element-6">
      <div className={`flex flex-col ${isEven ? 'lg:flex-row-reverse' : 'lg:flex-row'}`} data-id="element-7">
        {/* Image */}
        <div className="lg:w-2/5 bg-stone-50 p-6 flex items-center justify-center" data-id="element-8">
          <img src={bonus.image} alt={bonus.title} className="w-full max-w-xs rounded-2xl shadow-md" data-id="element-9" />
        </div>

        {/* Content */}
        <div className="lg:w-3/5 p-8 lg:p-10" data-id="element-10">
          {/* Badge */}
          <div className="flex items-center gap-2 mb-4" data-id="element-11">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700" data-id="element-12">
              <Gift className="w-3.5 h-3.5" data-id="element-13" />
              Bono #{bonus.number}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-stone-100 text-stone-500" data-id="element-14">
              {bonus.icon}
            </span>
          </div>

          {/* Title */}
          <h3 className="text-2xl lg:text-3xl font-extrabold text-stone-900 mb-1" data-id="element-15">
            {bonus.title}
          </h3>
          {bonus.subtitle && <p className="text-lg text-stone-500 font-medium mb-4" data-id="element-16">
              {bonus.subtitle}
            </p>}

          {/* Value */}
          <div className="flex items-center gap-3 mb-4" data-id="element-17">
            <span className="text-stone-400 line-through text-sm" data-id="element-18">
              Valor: {bonus.value}
            </span>
            <span className="text-emerald-600 font-bold text-lg" data-id="element-19">GRATIS</span>
          </div>

          {/* Description */}
          <p className="text-stone-600 mb-6 leading-relaxed" data-id="element-20">
            {bonus.description}
          </p>

          {/* Checklist */}
          <ul className="space-y-2" data-id="element-21">
            {bonus.checklist.map((item, i) => <li key={i} className="flex items-start gap-2 text-stone-700" data-id="element-22">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" data-id="element-23" />
                <span data-id="element-24">{item}</span>
              </li>)}
          </ul>
        </div>
      </div>
    </motion.div>;
}
export function BonusSection() {
  const totalValues = [{
    name: 'Pack de Plantillas Imprimibles',
    value: '$15.000'
  }, {
    name: 'Desafío 7 Días Sin Gritos',
    value: '$12.000'
  }, {
    name: 'El Mapa de la Calma',
    value: '$10.000'
  }, {
    name: 'Sistema Minimalista',
    value: '$11.000'
  }, {
    name: 'Protocolo Antirabietas',
    value: '$11.000'
  }];
  return <section id="bonos" className="py-10 bg-white" data-id="element-25">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-26">
        {/* Header */}
        <div className="text-center mb-10 max-w-3xl mx-auto" data-id="element-27">
          <span className="text-amber-500 font-bold text-sm uppercase tracking-widest mb-2 block" data-id="element-28">
            Además, Hoy Recibes
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-stone-900 mb-6" data-id="element-29">
            5 Bonos Premium GRATIS
          </h2>
          <p className="text-lg text-stone-600 mb-4" data-id="element-30">
            La mayoría de los padres no falla por falta de intención. Falla
            porque no tiene las herramientas correctas para aplicar los cambios
            en medio del caos del día a día.
          </p>
          <p className="text-lg text-stone-600 mb-8" data-id="element-31">
            Por eso creamos un sistema completo. Y para que puedas implementarlo
            desde el primer día, hoy también recibirás 5 recursos adicionales
            incluidos sin costo.
          </p>
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-full font-bold text-lg shadow-lg" data-id="element-32">
            <Gift className="w-5 h-5" data-id="element-33" />
            Valor total: $59.000 → Hoy GRATIS
          </div>
        </div>

        {/* Bonus Cards */}
        <div className="space-y-5 mb-10" data-id="element-34">
          {bonuses.map((bonus, index) => <BonusCard key={bonus.number} bonus={bonus} index={index} data-id="element-35" />)}
        </div>

        {/* Total Value Summary */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-2xl mx-auto bg-amber-50 border-2 border-amber-200 rounded-3xl p-8" data-id="element-36">
          <div className="flex items-center justify-center gap-2 mb-6" data-id="element-37">
            <Gift className="w-6 h-6 text-amber-500" data-id="element-38" />
            <h3 className="text-xl font-bold text-stone-800" data-id="element-39">
              Valor Total de los Bonos
            </h3>
          </div>

          <ul className="space-y-3 mb-6" data-id="element-40">
            {totalValues.map((item, i) => <li key={i} className="flex items-center justify-between text-stone-600" data-id="element-41">
                <span data-id="element-42">{item.name}</span>
                <span className="text-stone-400 line-through" data-id="element-43">
                  {item.value}
                </span>
              </li>)}
          </ul>

          <div className="border-t-2 border-amber-200 pt-6 mb-6" data-id="element-44">
            <div className="flex items-center justify-between" data-id="element-45">
              <span className="text-lg font-bold text-stone-800" data-id="element-46">Total:</span>
              <div className="flex items-center gap-3" data-id="element-47">
                <span className="text-xl text-stone-400 line-through" data-id="element-48">
                  $59.000
                </span>
                <span className="text-3xl font-black text-emerald-600" data-id="element-49">
                  GRATIS
                </span>
              </div>
            </div>
          </div>

          <p className="text-center text-stone-600 italic" data-id="element-50">
            Cuando accedes al Sistema Rutinas Visuales sin Gritos, obtienes un
            kit completo de herramientas diseñado para transformar la dinámica
            familiar desde el primer día.
          </p>
        </motion.div>
      </div>
    </section>;
}